// src/components/templates/diary/DiaryResourcesView.jsx
import React, { useState, useMemo, useRef } from 'react';
import {
  FileText, Video, Car, Home, TrendingUp, Landmark, Package, Gem,
  Plus, Search, MoreVertical, Edit2, Trash2, Eye, ExternalLink,
  Link as LinkIcon, Youtube, File, Image, FileSpreadsheet, Loader,
  X, Save, FolderOpen, Grid, List, Play, DollarSign,
  Tag, Briefcase, Upload, Globe
} from 'lucide-react';
import { Button, Card, Badge, Input, TextArea, Select, Modal } from './UIComponents';

// --- CONSTANTS ---
const ResourceTabs = {
  MEDIA: 'MEDIA',
  ASSETS: 'ASSETS'
};

const MediaType = {
  DOCUMENT: 'DOCUMENT',
  VIDEO: 'VIDEO',
  IMAGE: 'IMAGE',
  SPREADSHEET: 'SPREADSHEET',
  WEB_LINK: 'WEB_LINK',
  OTHER: 'OTHER'
};

const MediaTypeConfig = {
  [MediaType.DOCUMENT]: { icon: FileText, label: 'Document', color: 'gray' },
  [MediaType.VIDEO]: { icon: Video, label: 'Video', color: 'red' },
  [MediaType.IMAGE]: { icon: Image, label: 'Image', color: 'blue' },
  [MediaType.SPREADSHEET]: { icon: FileSpreadsheet, label: 'Spreadsheet', color: 'green' },
  [MediaType.WEB_LINK]: { icon: Globe, label: 'Web Link', color: 'purple' },
  [MediaType.OTHER]: { icon: File, label: 'Other', color: 'gray' }
};

const AssetCategory = {
  VEHICLE: 'VEHICLE',
  PROPERTY: 'PROPERTY',
  SHARES: 'SHARES',
  INVESTMENT: 'INVESTMENT',
  INSURANCE: 'INSURANCE',
  COLLECTIBLE: 'COLLECTIBLE',
  EQUIPMENT: 'EQUIPMENT',
  OTHER: 'OTHER'
};

const AssetCategoryConfig = {
  [AssetCategory.VEHICLE]: { icon: Car, label: 'Vehicles', color: 'blue' },
  [AssetCategory.PROPERTY]: { icon: Home, label: 'Properties', color: 'green' },
  [AssetCategory.SHARES]: { icon: TrendingUp, label: 'Shares & Stocks', color: 'purple' },
  [AssetCategory.INVESTMENT]: { icon: Landmark, label: 'Investments', color: 'amber' },
  [AssetCategory.INSURANCE]: { icon: Briefcase, label: 'Insurance', color: 'cyan' },
  [AssetCategory.COLLECTIBLE]: { icon: Gem, label: 'Collectibles', color: 'pink' },
  [AssetCategory.EQUIPMENT]: { icon: Package, label: 'Equipment', color: 'orange' },
  [AssetCategory.OTHER]: { icon: FolderOpen, label: 'Other Assets', color: 'gray' }
};

// --- HELPER FUNCTIONS ---
const getYoutubeId = (url) => {
  const match = url?.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
  return match ? match[1] : null;
};

const getYoutubeThumbnail = (url) => {
  const id = getYoutubeId(url);
  return id ? `https://img.youtube.com/vi/${id}/mqdefault.jpg` : null;
};

const isGoogleSheetsUrl = (url) => {
  return url?.includes('docs.google.com/spreadsheets') || url?.includes('sheets.google.com');
};

const formatCurrency = (amount, currency = 'ZAR') => {
  if (!amount) return '-';
  return new Intl.NumberFormat('en-ZA', { style: 'currency', currency }).format(amount);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-ZA', { 
    year: 'numeric', month: 'short', day: 'numeric' 
  });
};

const getFileIcon = (type, mimeType) => {
  const config = MediaTypeConfig[type];
  return config?.icon || File;
};

// --- UI COMPONENTS ---
const TabButton = ({ active, onClick, icon: Icon, label, count, customStyling }) => (
  <button
    onClick={onClick}
    style={{ color: active ? customStyling?.primaryColor : undefined }}
    className={`flex items-center gap-2 px-4 py-3 font-medium text-sm border-b-2 transition-all ${
      active
        ? 'border-indigo-600 bg-indigo-50/50'
        : 'text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-50'
    }`}
  >
    <Icon className="w-4 h-4" />
    {label}
    {count > 0 && (
      <span className={`px-2 py-0.5 rounded-full text-xs ${
        active ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'
      }`}>
        {count}
      </span>
    )}
  </button>
);

const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
      <Icon className="w-8 h-8 text-gray-400" />
    </div>
    <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-500 mb-6 max-w-sm">{description}</p>
    {action}
  </div>
);

const DropdownMenu = ({ children, trigger }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="relative">
      <div onClick={() => setIsOpen(!isOpen)}>{trigger}</div>
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 z-50 py-1">
            {React.Children.map(children, child =>
              React.cloneElement(child, { onClick: () => { child.props.onClick?.(); setIsOpen(false); } })
            )}
          </div>
        </>
      )}
    </div>
  );
};

const MenuItem = ({ icon: Icon, label, onClick, danger }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-2 text-sm text-left hover:bg-gray-50 ${
      danger ? 'text-red-600' : 'text-gray-700'
    }`}
  >
    <Icon className="w-4 h-4" />
    {label}
  </button>
);

// --- MEDIA TAB COMPONENTS ---
const MediaCard = ({ item, onEdit, onDelete, onView, viewMode }) => {
  const isYoutube = item.type === MediaType.VIDEO && item.youtubeUrl;
  const thumbnail = isYoutube ? getYoutubeThumbnail(item.youtubeUrl) : item.thumbnailUrl;
  const FileIcon = getFileIcon(item.type, item.mimeType);
  const config = MediaTypeConfig[item.type] || MediaTypeConfig[MediaType.OTHER];

  if (viewMode === 'list') {
    return (
      <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all">
        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
          {thumbnail ? (
            <img src={thumbnail} alt="" className="w-full h-full object-cover rounded-lg" />
          ) : (
            <FileIcon className="w-6 h-6 text-gray-400" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-gray-900 truncate">{item.name}</h4>
          <p className="text-sm text-gray-500 truncate">{item.description || 'No description'}</p>
        </div>
        <div className="flex items-center gap-3">
          {item.category && (
            <span className="px-2 py-1 bg-gray-100 rounded-full text-xs text-gray-600">
              {item.category}
            </span>
          )}
          <span className={`px-2 py-1 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-700`}>
            {config.label}
          </span>
          <span className="text-xs text-gray-400">{formatDate(item.createdAt)}</span>
          <DropdownMenu trigger={
            <button className="p-2 hover:bg-gray-100 rounded-lg">
              <MoreVertical className="w-4 h-4 text-gray-400" />
            </button>
          }>
            <MenuItem icon={Eye} label="View" onClick={() => onView(item)} />
            <MenuItem icon={Edit2} label="Edit" onClick={() => onEdit(item)} />
            <MenuItem icon={Trash2} label="Delete" onClick={() => onDelete(item.id)} danger />
          </DropdownMenu>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-gray-200 hover:shadow-md transition-all group">
      <div className="aspect-video bg-gray-100 relative">
        {thumbnail ? (
          <img src={thumbnail} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <FileIcon className="w-12 h-12 text-gray-300" />
          </div>
        )}
        {isYoutube && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="w-14 h-14 bg-red-600 rounded-full flex items-center justify-center">
              <Play className="w-6 h-6 text-white ml-1" />
            </div>
          </div>
        )}
        {item.type === MediaType.WEB_LINK && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="w-14 h-14 bg-purple-600 rounded-full flex items-center justify-center">
              <ExternalLink className="w-6 h-6 text-white" />
            </div>
          </div>
        )}
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <DropdownMenu trigger={
            <button className="p-2 bg-white/90 hover:bg-white rounded-lg shadow-sm">
              <MoreVertical className="w-4 h-4 text-gray-600" />
            </button>
          }>
            <MenuItem icon={Eye} label="View" onClick={() => onView(item)} />
            <MenuItem icon={Edit2} label="Edit" onClick={() => onEdit(item)} />
            <MenuItem icon={Trash2} label="Delete" onClick={() => onDelete(item.id)} danger />
          </DropdownMenu>
        </div>
      </div>
      <div className="p-4">
        <h4 className="font-medium text-gray-900 truncate">{item.name}</h4>
        <p className="text-sm text-gray-500 truncate mt-1">{item.description || 'No description'}</p>
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            item.type === MediaType.VIDEO ? 'bg-red-100 text-red-700' :
            item.type === MediaType.IMAGE ? 'bg-blue-100 text-blue-700' :
            item.type === MediaType.SPREADSHEET ? 'bg-green-100 text-green-700' :
            item.type === MediaType.WEB_LINK ? 'bg-purple-100 text-purple-700' :
            'bg-gray-100 text-gray-700'
          }`}>
            {config.label}
          </span>
          {item.category && (
            <span className="px-2 py-1 bg-indigo-50 text-indigo-600 rounded-full text-xs">
              {item.category}
            </span>
          )}
          <span className="text-xs text-gray-400">{formatDate(item.createdAt)}</span>
        </div>
      </div>
    </div>
  );
};

const MediaFormModal = ({ isOpen, onClose, onSave, mediaCategories = [], item, bookId }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: MediaType.DOCUMENT,
    youtubeUrl: '', 
    fileUrl: '',
    webUrl: '',
    googleSheetsUrl: '',
    thumbnailUrl: '',
    tags: [],
    category: '',
    ...item
  });
  const [tagInput, setTagInput] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [spreadsheetMode, setSpreadsheetMode] = useState(
    item?.googleSheetsUrl ? 'link' : 'upload'
  );
  const fileInputRef = useRef(null);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setUploadError('');
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !bookId) return;

    setUploading(true);
    setUploadError('');

    try {
      if (file.size > 10 * 1024 * 1024) {
        throw new Error('File size must be less than 10MB');
      }

      let fileType = 'documents';
      let mediaType = MediaType.DOCUMENT;
      
      if (file.type.startsWith('image/')) {
        fileType = 'images';
        mediaType = MediaType.IMAGE;
      } else if (file.type.includes('sheet') || file.type.includes('excel') || file.name.endsWith('.xlsx') || file.name.endsWith('.xls') || file.name.endsWith('.csv')) {
        fileType = 'spreadsheets';
        mediaType = MediaType.SPREADSHEET;
      } else if (file.type === 'application/pdf') {
        fileType = 'documents';
        mediaType = MediaType.DOCUMENT;
      }

      const storagePath = `pb-${bookId}/diary-media/${fileType}`;
      const { firestoreService } = await import('../../services/firestoreService');
      const result = await firestoreService.uploadFile(file, storagePath);

      setFormData(prev => ({
        ...prev,
        fileUrl: result.url,
        type: formData.type === MediaType.SPREADSHEET ? MediaType.SPREADSHEET : mediaType,
        name: prev.name || file.name.replace(/\.[^/.]+$/, '')
      }));
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error.message || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleAddCategory = () => {
    if (newCategory.trim()) {
      handleChange('category', newCategory.trim());
      setNewCategory('');
      setShowNewCategory(false);
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags?.includes(tagInput.trim())) {
      setFormData(prev => ({ ...prev, tags: [...(prev.tags || []), tagInput.trim()] }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag) => {
    setFormData(prev => ({ ...prev, tags: prev.tags?.filter(t => t !== tag) || [] }));
  };

  const handleSubmit = () => {
    if (!formData.name.trim()) {
      setUploadError('Name is required');
      return;
    }

    // Validation based on type
    if (formData.type === MediaType.VIDEO && !formData.youtubeUrl && !formData.fileUrl) {
      setUploadError('Please provide a YouTube URL or upload a video file');
      return;
    }

    if (formData.type === MediaType.WEB_LINK && !formData.webUrl) {
      setUploadError('Please provide a web URL');
      return;
    }

    if (formData.type === MediaType.SPREADSHEET) {
      if (spreadsheetMode === 'link' && !formData.googleSheetsUrl) {
        setUploadError('Please provide a Google Sheets URL');
        return;
      }
      if (spreadsheetMode === 'upload' && !formData.fileUrl) {
        setUploadError('Please upload a spreadsheet file');
        return;
      }
    }

    if ((formData.type === MediaType.DOCUMENT || formData.type === MediaType.IMAGE) && !formData.fileUrl) {
      setUploadError('Please upload a file');
      return;
    }

    onSave({
      ...formData,
      id: item?.id || Date.now().toString(),
      createdAt: item?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={item ? 'Edit Media' : 'Add Media'} size="md">
      <div className="space-y-4">
        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="Resource name"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {/* Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Type</label>
          <select
            value={formData.type}
            onChange={(e) => handleChange('type', e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value={MediaType.DOCUMENT}>📄 Document</option>
            <option value={MediaType.VIDEO}>🎬 Video</option>
            <option value={MediaType.IMAGE}>🖼️ Image</option>
            <option value={MediaType.SPREADSHEET}>📊 Spreadsheet</option>
            <option value={MediaType.WEB_LINK}>🔗 Web Link</option>
            <option value={MediaType.OTHER}>📁 Other</option>
          </select>
        </div>

        {/* Category with Add New */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Category</label>
          {!showNewCategory ? (
            <div className="flex gap-2">
              <select
                value={formData.category || ''}
                onChange={(e) => handleChange('category', e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Select category...</option>
                {mediaCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setShowNewCategory(true)}
                className="px-3 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-indigo-500 hover:text-indigo-600 transition-colors flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                New
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <input
                type="text"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCategory())}
                placeholder="New category name..."
                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                autoFocus
              />
              <button
                type="button"
                onClick={handleAddCategory}
                className="px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700"
              >
                Add
              </button>
              <button
                type="button"
                onClick={() => { setShowNewCategory(false); setNewCategory(''); }}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          )}
          {formData.category && (
            <div className="mt-2">
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-50 text-indigo-700 rounded-full text-sm">
                <Tag className="w-3 h-3" />
                {formData.category}
                <button onClick={() => handleChange('category', '')} className="hover:text-red-500 ml-1">
                  <X className="w-3 h-3" />
                </button>
              </span>
            </div>
          )}
        </div>

        {/* VIDEO - YouTube URL */}
        {formData.type === MediaType.VIDEO && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 text-left">YouTube URL</label>
            <div className="relative">
              <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="url"
                value={formData.youtubeUrl}
                onChange={(e) => handleChange('youtubeUrl', e.target.value)}
                placeholder="https://youtube.com/watch?v=..."
                className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            {getYoutubeThumbnail(formData.youtubeUrl) && (
              <div className="mt-2">
                <img 
                  src={getYoutubeThumbnail(formData.youtubeUrl)} 
                  alt="Video thumbnail" 
                  className="w-40 h-auto rounded-lg border border-gray-200"
                />
              </div>
            )}
          </div>
        )}

        {/* WEB LINK */}
        {formData.type === MediaType.WEB_LINK && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Web URL *</label>
            <div className="relative">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="url"
                value={formData.webUrl}
                onChange={(e) => handleChange('webUrl', e.target.value)}
                placeholder="https://example.com"
                className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
        )}

        {/* SPREADSHEET - File Upload OR Google Sheets Link */}
        {formData.type === MediaType.SPREADSHEET && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 text-left">Spreadsheet Source</label>
            
            {/* Toggle between modes */}
            <div className="flex gap-2 mb-3">
              <button
                type="button"
                onClick={() => setSpreadsheetMode('upload')}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  spreadsheetMode === 'upload'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Upload className="w-4 h-4" />
                Upload File
              </button>
              <button
                type="button"
                onClick={() => setSpreadsheetMode('link')}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  spreadsheetMode === 'link'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <FileSpreadsheet className="w-4 h-4" />
                Google Sheets
              </button>
            </div>

            {spreadsheetMode === 'upload' ? (
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileSelect}
                  accept=".xlsx,.xls,.csv,.ods"
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="w-full py-8 border-2 border-dashed border-gray-300 rounded-lg hover:border-indigo-500 hover:bg-indigo-50/50 transition-all flex flex-col items-center justify-center gap-2"
                >
                  {uploading ? (
                    <Loader className="w-6 h-6 text-indigo-600 animate-spin" />
                  ) : (
                    <Upload className="w-6 h-6 text-gray-400" />
                  )}
                  <span className="text-sm text-gray-600">
                    {uploading ? 'Uploading...' : 'Click to upload spreadsheet'}
                  </span>
                  <span className="text-xs text-gray-400">Excel, CSV, ODS supported</span>
                </button>
                {formData.fileUrl && (
                  <div className="mt-2 p-2 bg-green-50 rounded-lg flex items-center gap-2">
                    <FileSpreadsheet className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-700 flex-1 truncate">File uploaded</span>
                    <button onClick={() => handleChange('fileUrl', '')} className="text-red-500 hover:text-red-700">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <div className="relative">
                  <FileSpreadsheet className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-600" />
                  <input
                    type="url"
                    value={formData.googleSheetsUrl}
                    onChange={(e) => handleChange('googleSheetsUrl', e.target.value)}
                    placeholder="https://docs.google.com/spreadsheets/d/..."
                    className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Paste your Google Sheets sharing link (ensure sharing is enabled)
                </p>
              </div>
            )}
          </div>
        )}

        {/* DOCUMENT / IMAGE / OTHER - File Upload */}
        {(formData.type === MediaType.DOCUMENT || formData.type === MediaType.IMAGE || formData.type === MediaType.OTHER) && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
              Upload File {formData.type !== MediaType.OTHER && '*'}
            </label>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              accept={
                formData.type === MediaType.IMAGE ? 'image/*' :
                formData.type === MediaType.DOCUMENT ? '.pdf,.doc,.docx,.txt' :
                '*'
              }
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-full py-8 border-2 border-dashed border-gray-300 rounded-lg hover:border-indigo-500 hover:bg-indigo-50/50 transition-all flex flex-col items-center justify-center gap-2"
            >
              {uploading ? (
                <Loader className="w-6 h-6 text-indigo-600 animate-spin" />
              ) : (
                <Upload className="w-6 h-6 text-gray-400" />
              )}
              <span className="text-sm text-gray-600">
                {uploading ? 'Uploading...' : 'Click to upload file'}
              </span>
            </button>
            {formData.fileUrl && (
              <div className="mt-2 p-2 bg-green-50 rounded-lg flex items-center gap-2">
                <File className="w-4 h-4 text-green-600" />
                <span className="text-sm text-green-700 flex-1 truncate">File uploaded</span>
                <button onClick={() => handleChange('fileUrl', '')} className="text-red-500 hover:text-red-700">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Brief description..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
          />
        </div>

        {/* Tags */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Tags</label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
              placeholder="Add tag..."
              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <Button variant="secondary" onClick={handleAddTag}>Add</Button>
          </div>
          {formData.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {formData.tags.map(tag => (
                <span key={tag} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-full text-sm">
                  {tag}
                  <button onClick={() => handleRemoveTag(tag)} className="hover:text-red-500">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Error Message */}
        {uploadError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {uploadError}
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-4 mt-4 border-t border-gray-100">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={!formData.name.trim() || uploading}>
          <Save className="w-4 h-4" /> {item ? 'Update' : 'Add Media'}
        </Button>
      </div>
    </Modal>
  );
};

const MediaViewModal = ({ isOpen, onClose, item }) => {
  if (!item) return null;
  const isYoutube = item.type === MediaType.VIDEO && item.youtubeUrl;
  const youtubeId = getYoutubeId(item.youtubeUrl);
  const isGoogleSheet = item.type === MediaType.SPREADSHEET && item.googleSheetsUrl;

  const getEmbedUrl = (sheetsUrl) => {
    if (!sheetsUrl) return null;
    const match = sheetsUrl.match(/\/d\/([a-zA-Z0-9-_]+)/);
    if (match) {
      return `https://docs.google.com/spreadsheets/d/${match[1]}/preview`;
    }
    return null;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={item.name} size={isYoutube || isGoogleSheet ? 'lg' : 'md'}>
      {isYoutube && youtubeId && (
        <div className="aspect-video bg-black rounded-lg overflow-hidden mb-4">
          <iframe
            src={`https://www.youtube.com/embed/${youtubeId}`}
            title={item.name}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        </div>
      )}

      {isGoogleSheet && (
        <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden mb-4">
          <iframe
            src={getEmbedUrl(item.googleSheetsUrl)}
            title={item.name}
            frameBorder="0"
            className="w-full h-full"
          />
        </div>
      )}

      {item.type === MediaType.WEB_LINK && item.webUrl && (
        <div className="mb-4">
          <a 
            href={item.webUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Globe className="w-5 h-5" />
            Open Web Link
            <ExternalLink className="w-4 h-4" />
          </a>
          <p className="mt-2 text-sm text-gray-500 truncate">{item.webUrl}</p>
        </div>
      )}
      
      {!isYoutube && !isGoogleSheet && item.type !== MediaType.WEB_LINK && item.fileUrl && (
        <div className="mb-4">
          <a 
            href={item.fileUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            <ExternalLink className="w-4 h-4" />
            Open File
          </a>
        </div>
      )}

      {item.description && (
        <p className="text-gray-600 mb-4">{item.description}</p>
      )}

      {item.category && (
        <div className="mb-4">
          <span className="px-2 py-1 bg-indigo-50 text-indigo-600 rounded-full text-sm">
            {item.category}
          </span>
        </div>
      )}

      {item.tags?.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {item.tags.map(tag => (
            <span key={tag} className="px-2 py-1 bg-gray-100 rounded-full text-sm text-gray-600">
              {tag}
            </span>
          ))}
        </div>
      )}
    </Modal>
  );
};

// --- ASSET REGISTER COMPONENTS ---
const AssetCard = ({ asset, onEdit, onDelete, onView }) => {
  const config = AssetCategoryConfig[asset.category] || AssetCategoryConfig[AssetCategory.OTHER];
  const Icon = config.icon;

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-gray-200 hover:shadow-md transition-all">
      <div className={`h-2 bg-${config.color}-500`} />
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className={`w-10 h-10 rounded-lg bg-${config.color}-100 flex items-center justify-center`}>
            <Icon className={`w-5 h-5 text-${config.color}-600`} />
          </div>
          <DropdownMenu trigger={
            <button className="p-2 hover:bg-gray-100 rounded-lg">
              <MoreVertical className="w-4 h-4 text-gray-400" />
            </button>
          }>
            <MenuItem icon={Eye} label="View" onClick={() => onView(asset)} />
            <MenuItem icon={Edit2} label="Edit" onClick={() => onEdit(asset)} />
            <MenuItem icon={Trash2} label="Delete" onClick={() => onDelete(asset.id)} danger />
          </DropdownMenu>
        </div>
        
        <h4 className="font-semibold text-gray-900 mb-1">{asset.name}</h4>
        <p className="text-sm text-gray-500 mb-3">{config.label}</p>
        
        {asset.value && (
          <div className="flex items-center gap-2 text-lg font-bold text-green-600">
            <DollarSign className="w-5 h-5" />
            {formatCurrency(asset.value)}
          </div>
        )}
        
        <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-400">
          Added {formatDate(asset.createdAt)}
        </div>
      </div>
    </div>
  );
};

const AssetFormModal = ({ isOpen, onClose, onSave, asset }) => {
  const [formData, setFormData] = useState({
    name: '',
    category: AssetCategory.OTHER,
    description: '',
    value: '',
    purchaseDate: '',
    serialNumber: '',
    location: '',
    notes: '',
    ...asset
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    if (!formData.name.trim()) return;
    onSave({
      ...formData,
      value: parseFloat(formData.value) || 0,
      id: asset?.id || Date.now().toString(),
      createdAt: asset?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={asset ? 'Edit Asset' : 'Add Asset'} size="md">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Asset Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="e.g., 2020 Toyota Corolla"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Category</label>
            <select
              value={formData.category}
              onChange={(e) => handleChange('category', e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {Object.entries(AssetCategoryConfig).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Value (ZAR)</label>
            <input
              type="number"
              value={formData.value}
              onChange={(e) => handleChange('value', e.target.value)}
              placeholder="0.00"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Asset details..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Purchase Date</label>
            <input
              type="date"
              value={formData.purchaseDate}
              onChange={(e) => handleChange('purchaseDate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Serial/Reg Number</label>
            <input
              type="text"
              value={formData.serialNumber}
              onChange={(e) => handleChange('serialNumber', e.target.value)}
              placeholder="Optional"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Location</label>
          <input
            type="text"
            value={formData.location}
            onChange={(e) => handleChange('location', e.target.value)}
            placeholder="Where is this asset stored?"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            placeholder="Additional notes..."
            rows={2}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
          />
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 mt-4 border-t border-gray-100">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={!formData.name.trim()}>
          <Save className="w-4 h-4" /> {asset ? 'Update' : 'Add Asset'}
        </Button>
      </div>
    </Modal>
  );
};

const AssetViewModal = ({ isOpen, onClose, asset }) => {
  if (!asset) return null;
  const config = AssetCategoryConfig[asset.category] || AssetCategoryConfig[AssetCategory.OTHER];
  const Icon = config.icon;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={asset.name} size="md">
      <div className="space-y-4">
        <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
          <div className={`w-12 h-12 rounded-lg bg-${config.color}-100 flex items-center justify-center`}>
            <Icon className={`w-6 h-6 text-${config.color}-600`} />
          </div>
          <div>
            <p className="text-sm text-gray-500">{config.label}</p>
            {asset.value && (
              <p className="text-xl font-bold text-green-600">{formatCurrency(asset.value)}</p>
            )}
          </div>
        </div>

        {asset.description && (
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-1">Description</h4>
            <p className="text-gray-900">{asset.description}</p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          {asset.purchaseDate && (
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-1">Purchase Date</h4>
              <p className="text-gray-900">{formatDate(asset.purchaseDate)}</p>
            </div>
          )}
          {asset.serialNumber && (
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-1">Serial/Reg Number</h4>
              <p className="text-gray-900">{asset.serialNumber}</p>
            </div>
          )}
          {asset.location && (
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-1">Location</h4>
              <p className="text-gray-900">{asset.location}</p>
            </div>
          )}
        </div>

        {asset.notes && (
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-1">Notes</h4>
            <p className="text-gray-900">{asset.notes}</p>
          </div>
        )}

        <div className="text-xs text-gray-400 pt-4 border-t border-gray-100">
          Added {formatDate(asset.createdAt)}
          {asset.updatedAt && asset.updatedAt !== asset.createdAt && (
            <span> · Updated {formatDate(asset.updatedAt)}</span>
          )}
        </div>
      </div>
    </Modal>
  );
};

// --- MAIN COMPONENT ---
const DiaryResourcesView = ({
  bookId,
  customStyling = {},
  media = [],
  assets = [],
  onAddMedia,
  onUpdateMedia,
  onDeleteMedia,
  onAddAsset,
  onUpdateAsset,
  onDeleteAsset,
  diaryService
}) => {
  // State
  const [activeTab, setActiveTab] = useState(ResourceTabs.MEDIA);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('ALL');

  // Media states
  const [showMediaForm, setShowMediaForm] = useState(false);
  const [showMediaView, setShowMediaView] = useState(false);
  const [editingMedia, setEditingMedia] = useState(null);
  const [viewingMedia, setViewingMedia] = useState(null);

  // Asset states
  const [showAssetForm, setShowAssetForm] = useState(false);
  const [showAssetView, setShowAssetView] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [viewingAsset, setViewingAsset] = useState(null);

  // Filtered data
  const filteredMedia = useMemo(() => {
    return media.filter(item => {
      if (searchQuery && !item.name?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      if (filterCategory !== 'ALL' && item.type !== filterCategory) return false;
      return true;
    });
  }, [media, searchQuery, filterCategory]);

  const mediaCategories = useMemo(() => {
    const categories = media
      .map(m => m.category)
      .filter(Boolean);
    return Array.from(new Set(categories)).sort();
  }, [media]);

  const filteredAssets = useMemo(() => {
    return assets.filter(item => {
      if (searchQuery && !item.name?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      if (filterCategory !== 'ALL' && item.category !== filterCategory) return false;
      return true;
    });
  }, [assets, searchQuery, filterCategory]);

  // Calculate totals
  const totalAssetValue = useMemo(() => {
    return assets.reduce((sum, asset) => sum + (asset.value || 0), 0);
  }, [assets]);

  // Handlers
  const handleSaveMedia = (item) => {
    if (editingMedia) {
      onUpdateMedia?.(item);
    } else {
      onAddMedia?.(item);
    }
    setEditingMedia(null);
  };

  const handleSaveAsset = (item) => {
    if (editingAsset) {
      onUpdateAsset?.(item);
    } else {
      onAddAsset?.(item);
    }
    setEditingAsset(null);
  };

  const handleDeleteMedia = (id) => {
    if (window.confirm('Delete this media item?')) {
      onDeleteMedia?.(id);
    }
  };

  const handleDeleteAsset = (id) => {
    if (window.confirm('Delete this asset?')) {
      onDeleteAsset?.(id);
    }
  };

  return (
    <div className="h-full flex-1 overflow-y-auto flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Resources</h1>
            <p className="text-sm text-gray-500 mt-1">Manage documents, media, and assets</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-gray-200 -mb-4">
          <TabButton
            active={activeTab === ResourceTabs.MEDIA}
            onClick={() => { setActiveTab(ResourceTabs.MEDIA); setFilterCategory('ALL'); }}
            icon={FileText}
            label="Media"
            count={media.length}
            customStyling={customStyling}
          />
          <TabButton
            active={activeTab === ResourceTabs.ASSETS}
            onClick={() => { setActiveTab(ResourceTabs.ASSETS); setFilterCategory('ALL'); }}
            icon={Briefcase}
            label="Asset Register"
            count={assets.length}
            customStyling={customStyling}
          />
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* Category Filter */}
            {activeTab === ResourceTabs.MEDIA && (
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="ALL">All Types</option>
                {Object.entries(MediaTypeConfig).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
            )}

            {activeTab === ResourceTabs.ASSETS && (
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="ALL">All Categories</option>
                {Object.entries(AssetCategoryConfig).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
            )}

            {/* View Toggle */}
            <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
              >
                <Grid className="w-4 h-4 text-gray-600" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
              >
                <List className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Add Button */}
          {activeTab === ResourceTabs.MEDIA && (
            <Button onClick={() => { setEditingMedia(null); setShowMediaForm(true); }}
              style={{ backgroundColor: customStyling.primaryColor }}
            >
              <Plus className="w-4 h-4" /> Add Media
            </Button>
          )}
          {activeTab === ResourceTabs.ASSETS && (
            <Button onClick={() => { setEditingAsset(null); setShowAssetForm(true); }}
              style={{ backgroundColor: customStyling.primaryColor }}
            >
              <Plus className="w-4 h-4" /> Add Asset
            </Button>
          )}
        </div>

        {/* Asset Summary */}
        {activeTab === ResourceTabs.ASSETS && assets.length > 0 && (
          <div className="mt-3 flex items-center gap-6 text-sm">
            <div>
              <span className="text-gray-500">Total Assets:</span>
              <span className="ml-2 font-semibold">{assets.length}</span>
            </div>
            <div>
              <span className="text-gray-500">Total Value:</span>
              <span className="ml-2 font-semibold text-green-600">{formatCurrency(totalAssetValue)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Media Tab */}
        {activeTab === ResourceTabs.MEDIA && (
          <>
            {filteredMedia.length === 0 ? (
              <EmptyState
                icon={FileText}
                title="No media yet"
                description="Store documents, videos, links, and other files here for easy access."
                action={
                  <Button onClick={() => { setEditingMedia(null); setShowMediaForm(true); }}>
                    <Plus className="w-4 h-4" /> Add Media
                  </Button>
                }
              />
            ) : (
              <div className={viewMode === 'grid' 
                ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4' 
                : 'space-y-3'
              }>
                {filteredMedia.map(item => (
                  <MediaCard
                    key={item.id}
                    item={item}
                    viewMode={viewMode}
                    onEdit={(item) => { setEditingMedia(item); setShowMediaForm(true); }}
                    onDelete={handleDeleteMedia}
                    onView={(item) => { setViewingMedia(item); setShowMediaView(true); }}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* Assets Tab */}
        {activeTab === ResourceTabs.ASSETS && (
          <>
            {filteredAssets.length === 0 ? (
              <EmptyState
                icon={Briefcase}
                title="No assets registered"
                description="Keep track of your vehicles, properties, investments, and other valuables."
                action={
                  <Button onClick={() => { setEditingAsset(null); setShowAssetForm(true); }}>
                    <Plus className="w-4 h-4" /> Add Asset
                  </Button>
                }
              />
            ) : (
              <div className={viewMode === 'grid' 
                ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4' 
                : 'space-y-3'
              }>
                {filteredAssets.map(asset => (
                  <AssetCard
                    key={asset.id}
                    asset={asset}
                    onEdit={(asset) => { setEditingAsset(asset); setShowAssetForm(true); }}
                    onDelete={handleDeleteAsset}
                    onView={(asset) => { setViewingAsset(asset); setShowAssetView(true); }}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      <MediaFormModal
        isOpen={showMediaForm}
        onClose={() => { setShowMediaForm(false); setEditingMedia(null); }}
        onSave={handleSaveMedia}
        item={editingMedia}
        mediaCategories={mediaCategories}
        bookId={bookId}
      />

      <MediaViewModal
        isOpen={showMediaView}
        onClose={() => { setShowMediaView(false); setViewingMedia(null); }}
        item={viewingMedia}
      />

      <AssetFormModal
        isOpen={showAssetForm}
        onClose={() => { setShowAssetForm(false); setEditingAsset(null); }}
        onSave={handleSaveAsset}
        asset={editingAsset}
      />

      <AssetViewModal
        isOpen={showAssetView}
        onClose={() => { setShowAssetView(false); setViewingAsset(null); }}
        asset={viewingAsset}
      />
    </div>
  );
};

export default DiaryResourcesView;